import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetUserById, useToggleFollow } from '../features/user';
import { useUserContent } from '../features/content/hooks/useContent';
import { toast } from 'react-toastify';
import { formatCompactNumber } from '../utils';
import Avatar from '../components/common/Avatar';
import ContentCard from '../components/common/ContentCard';
import ContentModal from '../components/common/ContentModal';
import CommonHeader from '../components/layout/CommonHeader';
import { useContentModal } from '../hooks/useContentModal';
import { ProfilePhotoUrls } from '@/types';

interface UserProfileData {
  id: string;
  name: string;
  nickname: string;
  profile_photo_urls?: ProfilePhotoUrls;
  followers_count: number;
  following_count: number;
  applauses_count: number;
  is_followed: boolean;
  is_follower: boolean;
  is_me: boolean;
}

const UserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  
  // Local state to manage user data and optimistic updates
  const [userData, setUserData] = useState<UserProfileData | null>(null);
  // State to track if the background image failed to load
  const [imageLoadError, setImageLoadError] = useState<boolean>(false);
  
  // Fetch user data using the GraphQL hook
  const { data, loading, error } = useGetUserById(userId || '');
  
  // Fetch user content
  const { data: userContentData, loading: userContentLoading, error: userContentError } = useUserContent(userId || '', 1, 100);
  
  // Toggle follow mutation
  const [toggleFollow, { loading: toggleLoading }] = useToggleFollow();
  
  // Content modal functionality
  const { isModalOpen, selectedContent, openModal, closeModal } = useContentModal(
    userContentData?.userContent?.data || []
  );
  
  // Update local state when data changes
  useEffect(() => {
    if (data?.userById) {
      setUserData(data.userById);
      // Reset image error state when user data changes
      setImageLoadError(false);
    }
  }, [data]);
  
  useEffect(() => {
    // Set document title
    if (userData?.name) {
      document.title = `${userData.name} - zapOdeon`;
    } else {
      document.title = 'Profile - zapOdeon';
    }
  }, [userData?.name]);

  // Handle content click
  const handleContentClick = (contentId: string) => {
    const content = userContentData?.userContent?.data?.find(item => item.id === contentId);
    if (content) {
      openModal(content);
    }
  };

  // Handle follow/unfollow action
  const handleToggleFollow = async () => {
    if (!userId || toggleLoading) return;
    
    try {
      // Optimistic update
      setUserData((prev) => prev ? ({
        ...prev,
        is_followed: !prev.is_followed,
        followers_count: prev.is_followed 
          ? prev.followers_count - 1 
          : prev.followers_count + 1
      }) : prev);
      
      const result = await toggleFollow({
        variables: { userId }
      });
      
      if (result.data?.toggleFollow.success) {
        // Update with actual server response
        setUserData(result.data.toggleFollow.user as UserProfileData);
        toast.success(result.data.toggleFollow.message);
      } else {
        // Revert optimistic update on failure
        setUserData(data?.userById as UserProfileData || null);
        toast.error('Failed to update follow status');
      }
    } catch (error) {
      // Revert optimistic update on error
      setUserData(data?.userById as UserProfileData || null);
      console.error('Toggle follow error:', error);
    }
  };

  // Handle loading state
  if (loading) {
    return (
      <div className="h-dvh bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error || !userData) {
    return (
      <div className="h-dvh bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-4">Profile not found</h2>
          <p className="text-white/60 mb-6">The user you're looking for doesn't exist or has been removed.</p>
          <button 
            onClick={() => navigate('/discover')}
            className="btn btn-secondary"
          >
            Back to Discover
          </button>
        </div>
      </div>
    );
  }

  const user = userData;
  
  return (
    <div className="h-dvh bg-black text-white flex flex-col">
      {/* Header */}
      <CommonHeader
        backTo="/discover"
        rightButton={user.is_me ? {
          onClick: () => navigate('/settings'),
          className: "bg-transparent text-white border-0 relative",
          style: { top: '8px' },
          children: (
            <svg width="52" height="32" viewBox="0 0 52 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M27.3994 7.79464C27.0911 7.66797 26.6994 7.66797 25.9169 7.66797C25.1344 7.66797 24.7436 7.66797 24.4344 7.79464C24.231 7.87776 24.046 8.00029 23.8901 8.15517C23.7342 8.31006 23.6105 8.49425 23.5261 8.69714C23.4478 8.88297 23.4178 9.10047 23.4053 9.4163C23.3993 9.64505 23.3353 9.86854 23.2193 10.0658C23.1033 10.263 22.939 10.4275 22.7419 10.5438C22.5409 10.6556 22.3149 10.7148 22.0849 10.7159C21.8549 10.7171 21.6283 10.6602 21.4261 10.5505C21.1444 10.4021 20.9403 10.3205 20.7386 10.2938C20.2974 10.2362 19.8513 10.3548 19.4969 10.6238C19.2319 10.8255 19.0361 11.1621 18.6453 11.8346C18.2536 12.5071 18.0586 12.843 18.0144 13.1721C17.9561 13.6105 18.0761 14.0538 18.3478 14.4046C18.4711 14.5646 18.6453 14.6988 18.9144 14.8671C19.3119 15.1146 19.5669 15.5363 19.5669 16.0013C19.5669 16.4663 19.3119 16.888 18.9153 17.1346C18.6453 17.3038 18.4711 17.438 18.3469 17.598C18.2132 17.7715 18.1151 17.9696 18.058 18.1811C18.0009 18.3926 17.9861 18.6133 18.0144 18.8305C18.0586 19.1588 18.2536 19.4955 18.6453 20.168C19.0369 20.8405 19.2319 21.1763 19.4969 21.3788C19.8503 21.648 20.2969 21.7663 20.7386 21.7088C20.9403 21.6821 21.1444 21.6005 21.4261 21.4521C21.6284 21.3423 21.8551 21.2853 22.0853 21.2865C22.3155 21.2876 22.5416 21.3469 22.7428 21.4588C23.1478 21.6921 23.3878 22.1213 23.4053 22.5863C23.4178 22.903 23.4478 23.1196 23.5261 23.3055C23.6961 23.7138 24.0228 24.0388 24.4344 24.208C24.7428 24.3346 25.1344 24.3346 25.9169 24.3346C26.6994 24.3346 27.0911 24.3346 27.3994 24.208C27.6029 24.1248 27.7878 24.0023 27.9437 23.8474C28.0996 23.6925 28.2233 23.5084 28.3078 23.3055C28.3861 23.1196 28.4161 22.903 28.4286 22.5863C28.4453 22.1213 28.6861 21.6913 29.0919 21.4588C29.293 21.347 29.519 21.2878 29.749 21.2867C29.979 21.2855 30.2056 21.3424 30.4078 21.4521C30.6894 21.6005 30.8936 21.6821 31.0953 21.7088C31.5369 21.7671 31.9836 21.648 32.3369 21.3788C32.6019 21.1771 32.7978 20.8405 33.1886 20.168C33.5803 19.4955 33.7753 19.1596 33.8194 18.8305C33.8476 18.6132 33.8327 18.3925 33.7755 18.181C33.7183 17.9695 33.6199 17.7714 33.4861 17.598C33.3628 17.438 33.1886 17.3038 32.9194 17.1355C32.5219 16.888 32.2669 16.4663 32.2669 16.0013C32.2669 15.5363 32.5219 15.1146 32.9186 14.868C33.1886 14.6988 33.3628 14.5646 33.4869 14.4046C33.6206 14.2311 33.7188 14.033 33.7759 13.8215C33.8329 13.61 33.8477 13.3893 33.8194 13.1721C33.7753 12.8438 33.5803 12.5071 33.1886 11.8346C32.7969 11.1621 32.6019 10.8263 32.3369 10.6238C31.9825 10.3548 31.5364 10.2362 31.0953 10.2938C30.8936 10.3205 30.6894 10.4021 30.4078 10.5505C30.2055 10.6603 29.9787 10.7173 29.7486 10.7162C29.5184 10.715 29.2922 10.6557 29.0911 10.5438C28.8942 10.4274 28.7301 10.2628 28.6142 10.0656C28.4983 9.8684 28.4345 9.64497 28.4286 9.4163C28.4161 9.09964 28.3861 8.88297 28.3078 8.69714C28.2233 8.49425 28.0996 8.31006 27.9437 8.15517C27.7878 8.00029 27.6029 7.87776 27.3994 7.79464ZM25.9169 18.5013C27.3086 18.5013 28.4361 17.3821 28.4361 16.0013C28.4361 14.6205 27.3078 13.5013 25.9169 13.5013C24.5261 13.5013 23.3978 14.6205 23.3978 16.0013C23.3978 17.3821 24.5261 18.5013 25.9169 18.5013Z" fill="white"/>
            </svg>
          )
        } : {
          onClick: () => {},
          className: "bg-transparent text-white border-0 relative",
          style: { top: '8px' },
          children: (
            <svg width="52" height="32" viewBox="0 0 52 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21.8333 15.9987C21.8333 16.4407 21.6577 16.8646 21.3452 17.1772C21.0326 17.4898 20.6087 17.6654 20.1667 17.6654C19.7246 17.6654 19.3007 17.4898 18.9882 17.1772C18.6756 16.8646 18.5 16.4407 18.5 15.9987C18.5 15.5567 18.6756 15.1327 18.9882 14.8202C19.3007 14.5076 19.7246 14.332 20.1667 14.332C20.6087 14.332 21.0326 14.5076 21.3452 14.8202C21.6577 15.1327 21.8333 15.5567 21.8333 15.9987ZM27.6667 15.9987C27.6667 16.4407 27.4911 16.8646 27.1785 17.1772C26.866 17.4898 26.442 17.6654 26 17.6654C25.558 17.6654 25.134 17.4898 24.8215 17.1772C24.5089 16.8646 24.3333 16.4407 24.3333 15.9987C24.3333 15.5567 24.5089 15.1327 24.8215 14.8202C25.134 14.5076 25.558 14.332 26 14.332C26.442 14.332 26.866 14.5076 27.1785 14.8202C27.4911 15.1327 27.6667 15.5567 27.6667 15.9987ZM33.5 15.9987C33.5 16.4407 33.3244 16.8646 33.0118 17.1772C32.6993 17.4898 32.2754 17.6654 31.8333 17.6654C31.3913 17.6654 30.9674 17.4898 30.6548 17.1772C30.3423 16.8646 30.1667 16.4407 30.1667 15.9987C30.1667 15.5567 30.3423 15.1327 30.6548 14.8202C30.9674 14.5076 31.3913 14.332 31.8333 14.332C32.2754 14.332 32.6993 14.5076 33.0118 14.8202C33.3244 15.1327 33.5 15.5567 33.5 15.9987Z" fill="white"/>
            </svg>
          )
                }}
      />
  
      {/* Photo Block - 60% height */}
      <div 
        className="relative w-full flex-shrink-0"
        style={{ 
          height: '60vh',
          minHeight: '60vh'
        }}
      >
        {/* Background Image or Avatar Fallback */}
        {((user.profile_photo_urls?.large || user.profile_photo_urls?.medium || user.profile_photo_urls?.original) && !imageLoadError) ? (
          <>
            {/* Background Image */}
            <div 
              className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${user.profile_photo_urls?.large || user.profile_photo_urls?.medium || user.profile_photo_urls?.original})` }}
            />
            {/* Hidden img element for error handling */}
            <img
              src={user.profile_photo_urls?.large || user.profile_photo_urls?.medium || user.profile_photo_urls?.original}
              alt=""
              className="hidden"
              onError={() => setImageLoadError(true)}
            />
            {/* Gradient Overlay */}
            <div 
              className="absolute inset-0 w-full h-full"
              style={{
                background: 'linear-gradient(180deg, rgba(21, 21, 21, 0.50) 0%, rgba(21, 21, 21, 0.00) 22.57%, #151515 94.06%)'
              }}
            />
          </>
        ) : (
          <>
            {/* Avatar Fallback with Background */}
            <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-gray-800 to-black flex items-center justify-center">
              <Avatar
                name={user.name}
                profilePhotoUrls={user.profile_photo_urls}
                shape="square"
                className="w-full h-full"
                style={{ maxWidth: '100vw', maxHeight: '60vh' }}
              />
            </div>
            {/* Gradient Overlay for consistency */}
            <div 
              className="absolute inset-0 w-full h-full"
              style={{
                background: 'linear-gradient(180deg, rgba(21, 21, 21, 0.30) 0%, rgba(21, 21, 21, 0.00) 22.57%, #151515 94.06%)'
              }}
            />
          </>
        )}
        
        {/* Username and Handle at bottom */}
        <div className="absolute bottom-6 left-0 right-0 z-10 px-6">
          <h1 
            className="text-white mb-1"
            style={{
              fontFamily: '"Aeonik TRIAL"',
              fontSize: '28px',
              fontWeight: 600,
              lineHeight: '32px',
              letterSpacing: '-0.35px'
            }}
          >
            {user.name}
          </h1>
          <h2 
            className="text-white/60 mb-4"
            style={{
              fontFamily: '"Aeonik TRIAL"',
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '18px',
              letterSpacing: '-0.2px'
            }}
          >
            @{user.nickname}
          </h2>
          
          {/* Stats */}
          <div className="flex w-full">
            <div className="flex-1 text-center">
              <div className="text-white font-semibold text-lg">{formatCompactNumber(user.applauses_count)}</div>
              <div className="text-white/60 text-sm">Applause</div>
            </div>
            <div 
              className="flex-1 text-center cursor-pointer"
              onClick={() => navigate(`/followers/${user.id}`)}
            >
              <div className="text-white font-semibold text-lg">{formatCompactNumber(user.followers_count)}</div>
              <div className="text-white/60 text-sm">Followers</div>
            </div>
            <div 
              className="flex-1 text-center cursor-pointer"
              onClick={() => navigate(`/following/${user.id}`)}
            >
              <div className="text-white font-semibold text-lg">{formatCompactNumber(user.following_count)}</div>
              <div className="text-white/60 text-sm">Following</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section - remaining height */}
      <div className="flex-1 bg-black overflow-y-auto min-h-0">
        {/* Content Area */}
        <div className="px-[8px] pt-[8px] pb-20">
          {/* Loading state */}
          {userContentLoading && (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          )}

          {/* Error state */}
          {userContentError && (
            <div className="flex items-center justify-center h-32 text-white/60">
              <p>Failed to load content.</p>
            </div>
          )}

          {/* No content state */}
          {!userContentLoading && !userContentError && (!userContentData?.userContent?.data || userContentData.userContent.data.length === 0) && (
            <div className="flex items-center justify-center h-32 text-white/60">
              <p>No content yet</p>
            </div>
          )}

          {/* Content grid */}
          {userContentData?.userContent?.data && userContentData.userContent.data.length > 0 && (
            <div className="grid grid-cols-3 gap-x-[8px] gap-y-[8px]">
              {userContentData.userContent.data.map((content) => (
                <div key={content.id} className="aspect-square">
                  <ContentCard 
                    content={content} 
                    onClick={handleContentClick}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fixed bottom navigation bar */}
      <div 
        className="fixed bottom-[50px] left-1/2 transform -translate-x-1/2 z-10 h-[64px] max-w-[216px] w-full rounded-[32px]"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.76)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)'
        }}
      >
        <div className="flex items-center justify-center h-full">
          {/* Follow/Edit Profile Button - Centered */}
          {!user.is_me ? (
            !user.is_followed ? (
              <button 
                className="btn btn-primary"
                onClick={handleToggleFollow}
                disabled={toggleLoading}
              >
                {toggleLoading ? 'Following...' : 'Follow'}
              </button>
            ) : (
              <button 
                className="btn btn-secondary"
                onClick={handleToggleFollow}
                disabled={toggleLoading}
              >
                {toggleLoading ? 'Unfollowing...' : 'Following'}
              </button>
            )
          ) : (
            <button 
              onClick={() => navigate('/edit-profile')}
              className="btn btn-secondary"
            >
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* Content Modal */}
      {selectedContent && (
        <ContentModal
          content={selectedContent}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default UserProfile; 